import { useMemo, useState } from 'react';
import { CircleDashed, FileSpreadsheet, RulerDimensionLine, type LucideIcon } from 'lucide-react';

import { OptimizedImage } from '@/shared/ui/OptimizedImage';
import { SOCIAL_MEDIA_PLATFORM_CONFIG } from '@/shared/constants';
import type { AudienceInsightsTableProps, AudienceMetric } from './AudienceInsightsTable.types';
import { useTranslation } from 'react-i18next';

// Match the icons used per metric in the Media Consumption section.
const METRIC_ICON: Record<AudienceMetric, LucideIcon> = {
  audiencePercentage: FileSpreadsheet,
  index: RulerDimensionLine,
};

// Maps a row label to its key in SOCIAL_MEDIA_PLATFORM_CONFIG when they differ.
const PLATFORM_LABEL_ALIASES: Record<string, string> = {
  // eslint-disable-next-line i18next/no-literal-string -- lookup key into SOCIAL_MEDIA_PLATFORM_CONFIG, which resolves an icon path
  X: 'Twitter/X',
};

const resolvePlatformIcon = (label: string): string | undefined => {
  const key = PLATFORM_LABEL_ALIASES[label] ?? label;
  return (SOCIAL_MEDIA_PLATFORM_CONFIG as Record<string, { image: string } | undefined>)[key]
    ?.image;
};

const DEFAULT_MAX_ROWS = 10;

export function AudienceInsightsTable({
  insights,
  maxRows = DEFAULT_MAX_ROWS,
  showPlatformIcons = false,
}: AudienceInsightsTableProps) {
  const { t } = useTranslation('common');

  const [metric, setMetric] = useState<AudienceMetric>('audiencePercentage');

  const metricOptions: { value: AudienceMetric; label: string }[] = useMemo(
    () => [
      { value: 'audiencePercentage', label: t('components.audienceInsightsTable.audience') },
      { value: 'index', label: t('components.audienceInsightsTable.index') },
    ],
    [t],
  );

  // Sort by the selected metric (highest first), then cap to the top `maxRows`.
  const sortedInsights = useMemo(
    () => [...insights].sort((a, b) => b[metric] - a[metric]).slice(0, maxRows),
    [insights, metric, maxRows],
  );

  return (
    <div className="flex min-h-80 flex-col overflow-hidden rounded-2xl border border-border bg-muted lg:min-h-0">
      <div className="flex items-center justify-between gap-3 px-6 py-5">
        <h2 className="text-xl font-semibold">
          {t('components.audienceInsightsTable.audienceInsights')}
        </h2>

        {/* Audience % / Index toggle */}
        <div className="flex items-center rounded-lg bg-accent p-0.5">
          {metricOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={metric === option.value}
              onClick={() => setMetric(option.value)}
              className={`rounded-md px-3 py-1 text-sm font-medium transition-colors duration-fast ease-out ${
                metric === option.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Shared grid: every row is `display: contents`, so cells line up under one
          column template regardless of value width. The metric icon and value are
          separate columns, so the icon stays in a fixed position across rows
          instead of drifting with the value width. Columns:
          rank | platform-icon? | label | metric-icon | value */}
      <div
        className="grid min-h-0 flex-1 items-center gap-x-2 overflow-y-auto"
        style={{
          gridTemplateColumns: `min-content${
            showPlatformIcons ? ' min-content' : ''
          } minmax(0, 1fr) min-content max-content`,
        }}
      >
        {sortedInsights.map((insight, idx) => {
          const Icon = METRIC_ICON[metric];
          const primaryValue =
            metric === 'audiencePercentage' ? `${insight.audiencePercentage}%` : insight.index;
          const platformIcon = showPlatformIcons
            ? resolvePlatformIcon(insight.platform ?? insight.label)
            : undefined;
          return (
            <div key={`${insight.label}-${idx}`} className="contents *:py-3">
              <span className="min-w-4 pl-6 text-sm text-muted-foreground">{idx + 1}</span>
              {showPlatformIcons &&
                (platformIcon ? (
                  <OptimizedImage src={platformIcon} alt={insight.label} width={20} height={20} />
                ) : (
                  <CircleDashed className="size-5 text-muted-foreground" />
                ))}
              <span className="text-base text-foreground">{insight.label}</span>
              <span className="flex items-center text-muted-foreground">
                <Icon className="size-4 shrink-0" />
              </span>
              <span className="pr-6 text-muted-foreground">{primaryValue}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

AudienceInsightsTable.displayName = 'AudienceInsightsTable';
